import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { TenantProfileCreateStore } from "../../store";
import { PersonType } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { TenantProfileLegalNameCustomField } from "../custom/TenantProfileLegalNameCustomField";
import { CreateEntityDialog } from "../../../common/controls";
import { PublisherCreate } from "../../../publisher/components/create/PublisherCreate";
import { PublisherCreateStore } from "../../../publisher/store";
import { InputStringMask } from "../../../common/controls";
import { InputString } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { CountryCreate } from "../../../country/components/create/CountryCreate";
import { CountryCreateStore } from "../../../country/store";
import { ProvinceStateCreate } from "../../../provinceState/components/create/ProvinceStateCreate";
import { ProvinceStateCreateStore } from "../../../provinceState/store";
import { VatStatusCreate } from "../../../vatStatus/components/create/VatStatusCreate";
import { VatStatusCreateStore } from "../../../vatStatus/store";
import { GrossReceiptsStatusCreate } from "../../../grossReceiptsStatus/components/create/GrossReceiptsStatusCreate";
import { GrossReceiptsStatusCreateStore } from "../../../grossReceiptsStatus/store";
import { CurrencyCreate } from "../../../currency/components/create/CurrencyCreate";
import { CurrencyCreateStore } from "../../../currency/store";
import { InputSecret } from "../../../common/controls";

export type TenantProfileCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  personTypeError?: string;
  personTypeOptions: { id: string; displayId: string }[];
  personType: PersonType | "";
  onPersonTypeChange?: (value: PersonType | "") => void;
  legalNameSelectedValue?: { displayId: string; id: string } | null;
  onLegalNameChange?: (value: string) => void;
  legalName: string;
  legalNameError?: string;
  ownPublisherSelectedValue?: { displayId: string; id: string } | null;
  onOwnPublisherSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onOwnPublisherChange?: (value: string) => void;
  ownPublisherOptions?: { displayId: string; id: string }[];
  ownPublisherError?: string;
  businessIdSelectedValue?: { displayId: string; id: string } | null;
  onBusinessIdChange?: (value: string) => void;
  businessId: string;
  businessIdError?: string;
  legalEmailSelectedValue?: { displayId: string; id: string } | null;
  onLegalEmailChange?: (value: string) => void;
  legalEmail: string;
  legalEmailError?: string;
  streetAndNumberSelectedValue?: { displayId: string; id: string } | null;
  onStreetAndNumberChange?: (value: string) => void;
  streetAndNumber: string;
  streetAndNumberError?: string;
  floorSelectedValue?: { displayId: string; id: string } | null;
  onFloorChange?: (value: string) => void;
  floor: string;
  floorError?: string;
  suiteSelectedValue?: { displayId: string; id: string } | null;
  onSuiteChange?: (value: string) => void;
  suite: string;
  suiteError?: string;
  postalCodeSelectedValue?: { displayId: string; id: string } | null;
  onPostalCodeChange?: (value: string) => void;
  postalCode: string;
  postalCodeError?: string;
  countrySelectedValue?: { displayId: string; id: string } | null;
  onCountrySelected?: (value: { displayId: string; id: string } | null) => void;
  onCountryChange?: (value: string) => void;
  countryOptions?: { displayId: string; id: string }[];
  countryError?: string;
  provinceSelectedValue?: { displayId: string; id: string } | null;
  onProvinceSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onProvinceChange?: (value: string) => void;
  provinceOptions?: { displayId: string; id: string }[];
  provinceError?: string;
  citySelectedValue?: { displayId: string; id: string } | null;
  onCityChange?: (value: string) => void;
  city: string;
  cityError?: string;
  vatStatusSelectedValue?: { displayId: string; id: string } | null;
  onVatStatusSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onVatStatusChange?: (value: string) => void;
  vatStatusOptions?: { displayId: string; id: string }[];
  vatStatusError?: string;
  taxIdSelectedValue?: { displayId: string; id: string } | null;
  onTaxIdChange?: (value: string) => void;
  taxId: string;
  taxIdError?: string;
  grossReceiptsStatusSelectedValue?: { displayId: string; id: string } | null;
  onGrossReceiptsStatusSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onGrossReceiptsStatusChange?: (value: string) => void;
  grossReceiptsStatusOptions?: { displayId: string; id: string }[];
  grossReceiptsStatusError?: string;
  grossReceiptsIdSelectedValue?: { displayId: string; id: string } | null;
  onGrossReceiptsIdChange?: (value: string) => void;
  grossReceiptsId: string;
  grossReceiptsIdError?: string;
  billingEmailSelectedValue?: { displayId: string; id: string } | null;
  onBillingEmailChange?: (value: string) => void;
  billingEmail: string;
  billingEmailError?: string;
  billingCurrencySelectedValue?: { displayId: string; id: string } | null;
  onBillingCurrencySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onBillingCurrencyChange?: (value: string) => void;
  billingCurrencyOptions?: { displayId: string; id: string }[];
  billingCurrencyError?: string;
  printerNameSelectedValue?: { displayId: string; id: string } | null;
  onPrinterNameChange?: (value: string) => void;
  printerName: string;
  printerNameError?: string;
  printerStreetAndNumberSelectedValue?: {
    displayId: string;
    id: string;
  } | null;
  onPrinterStreetAndNumberChange?: (value: string) => void;
  printerStreetAndNumber: string;
  printerStreetAndNumberError?: string;
  printerFloorSelectedValue?: { displayId: string; id: string } | null;
  onPrinterFloorChange?: (value: string) => void;
  printerFloor: string;
  printerFloorError?: string;
  printerSuiteSelectedValue?: { displayId: string; id: string } | null;
  onPrinterSuiteChange?: (value: string) => void;
  printerSuite: string;
  printerSuiteError?: string;
  printerPostalCodeSelectedValue?: { displayId: string; id: string } | null;
  onPrinterPostalCodeChange?: (value: string) => void;
  printerPostalCode: string;
  printerPostalCodeError?: string;
  printerCountrySelectedValue?: { displayId: string; id: string } | null;
  onPrinterCountrySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onPrinterCountryChange?: (value: string) => void;
  printerCountryOptions?: { displayId: string; id: string }[];
  printerCountryError?: string;
  printerProvinceSelectedValue?: { displayId: string; id: string } | null;
  onPrinterProvinceSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onPrinterProvinceChange?: (value: string) => void;
  printerProvinceOptions?: { displayId: string; id: string }[];
  printerProvinceError?: string;
  printerCitySelectedValue?: { displayId: string; id: string } | null;
  onPrinterCityChange?: (value: string) => void;
  printerCity: string;
  printerCityError?: string;
  sadaicFtpUrlSelectedValue?: { displayId: string; id: string } | null;
  onSadaicFtpUrlChange?: (value: string) => void;
  sadaicFtpUrl: string;
  sadaicFtpUrlError?: string;
  sadaicFtpUserSelectedValue?: { displayId: string; id: string } | null;
  onSadaicFtpUserChange?: (value: string) => void;
  sadaicFtpUser: string;
  sadaicFtpUserError?: string;
  sadaicFtpPasswordSelectedValue?: { displayId: string; id: string } | null;
  onSadaicFtpPasswordChange?: (value: string) => void;
  sadaicFtpPassword: string;
  sadaicFtpPasswordError?: string;
  sadaicEmailSelectedValue?: { displayId: string; id: string } | null;
  onSadaicEmailChange?: (value: string) => void;
  sadaicEmail: string;
  sadaicEmailError?: string;
  tenantProfileCreateStore: TenantProfileCreateStore;
};

export const TenantProfileCreateForm: React.FC<TenantProfileCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      personTypeError,
      personTypeOptions = [],
      personType = "" as PersonType | "",
      onPersonTypeChange = () => {},
      legalName = "",
      legalNameError,
      onLegalNameChange = () => {},
      onOwnPublisherSelected = () => {},
      ownPublisherSelectedValue = null,
      ownPublisherOptions = [],
      ownPublisherError,
      onOwnPublisherChange = () => {},
      businessId = "",
      businessIdError,
      onBusinessIdChange = () => {},
      legalEmail = "",
      legalEmailError,
      onLegalEmailChange = () => {},
      streetAndNumber = "",
      streetAndNumberError,
      onStreetAndNumberChange = () => {},
      floor = "",
      floorError,
      onFloorChange = () => {},
      suite = "",
      suiteError,
      onSuiteChange = () => {},
      postalCode = "",
      postalCodeError,
      onPostalCodeChange = () => {},
      onCountrySelected = () => {},
      countrySelectedValue = null,
      countryOptions = [],
      countryError,
      onCountryChange = () => {},
      onProvinceSelected = () => {},
      provinceSelectedValue = null,
      provinceOptions = [],
      provinceError,
      onProvinceChange = () => {},
      city = "",
      cityError,
      onCityChange = () => {},
      onVatStatusSelected = () => {},
      vatStatusSelectedValue = null,
      vatStatusOptions = [],
      vatStatusError,
      onVatStatusChange = () => {},
      taxId = "",
      taxIdError,
      onTaxIdChange = () => {},
      onGrossReceiptsStatusSelected = () => {},
      grossReceiptsStatusSelectedValue = null,
      grossReceiptsStatusOptions = [],
      grossReceiptsStatusError,
      onGrossReceiptsStatusChange = () => {},
      grossReceiptsId = "",
      grossReceiptsIdError,
      onGrossReceiptsIdChange = () => {},
      billingEmail = "",
      billingEmailError,
      onBillingEmailChange = () => {},
      onBillingCurrencySelected = () => {},
      billingCurrencySelectedValue = null,
      billingCurrencyOptions = [],
      billingCurrencyError,
      onBillingCurrencyChange = () => {},
      printerName = "",
      printerNameError,
      onPrinterNameChange = () => {},
      printerStreetAndNumber = "",
      printerStreetAndNumberError,
      onPrinterStreetAndNumberChange = () => {},
      printerFloor = "",
      printerFloorError,
      onPrinterFloorChange = () => {},
      printerSuite = "",
      printerSuiteError,
      onPrinterSuiteChange = () => {},
      printerPostalCode = "",
      printerPostalCodeError,
      onPrinterPostalCodeChange = () => {},
      onPrinterCountrySelected = () => {},
      printerCountrySelectedValue = null,
      printerCountryOptions = [],
      printerCountryError,
      onPrinterCountryChange = () => {},
      onPrinterProvinceSelected = () => {},
      printerProvinceSelectedValue = null,
      printerProvinceOptions = [],
      printerProvinceError,
      onPrinterProvinceChange = () => {},
      printerCity = "",
      printerCityError,
      onPrinterCityChange = () => {},
      sadaicFtpUrl = "",
      sadaicFtpUrlError,
      onSadaicFtpUrlChange = () => {},
      sadaicFtpUser = "",
      sadaicFtpUserError,
      onSadaicFtpUserChange = () => {},
      sadaicFtpPassword = "",
      sadaicFtpPasswordError,
      onSadaicFtpPasswordChange = () => {},
      sadaicEmail = "",
      sadaicEmailError,
      onSadaicEmailChange = () => {},
      tenantProfileCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormRecordProvider
              values={{
                personType,
                legalName,
                ownPublisher: ownPublisherSelectedValue,
                businessId,
                legalEmail,
                streetAndNumber,
                floor,
                suite,
                postalCode,
                country: countrySelectedValue,
                province: provinceSelectedValue,
                city,
                vatStatus: vatStatusSelectedValue,
                taxId,
                grossReceiptsStatus: grossReceiptsStatusSelectedValue,
                grossReceiptsId,
                billingEmail,
                billingCurrency: billingCurrencySelectedValue,
                printerName,
                printerStreetAndNumber,
                printerFloor,
                printerSuite,
                printerPostalCode,
                printerCountry: printerCountrySelectedValue,
                printerProvince: printerProvinceSelectedValue,
                printerCity,
                sadaicFtpUrl,
                sadaicFtpUser,
                sadaicFtpPassword,
                sadaicEmail,
              }}
            >
              <FormExtras
                entity="TenantProfile"
                mode="create"
                values={{
                  personType,
                  legalName,
                  ownPublisher: ownPublisherSelectedValue,
                  businessId,
                  legalEmail,
                  streetAndNumber,
                  floor,
                  suite,
                  postalCode,
                  country: countrySelectedValue,
                  province: provinceSelectedValue,
                  city,
                  vatStatus: vatStatusSelectedValue,
                  taxId,
                  grossReceiptsStatus: grossReceiptsStatusSelectedValue,
                  grossReceiptsId,
                  billingEmail,
                  billingCurrency: billingCurrencySelectedValue,
                  printerName,
                  printerStreetAndNumber,
                  printerFloor,
                  printerSuite,
                  printerPostalCode,
                  printerCountry: printerCountrySelectedValue,
                  printerProvince: printerProvinceSelectedValue,
                  printerCity,
                  sadaicFtpUrl,
                  sadaicFtpUser,
                  sadaicFtpPassword,
                  sadaicEmail,
                }}
                options={{
                  ownPublisher: ownPublisherOptions,
                  country: countryOptions,
                  province: provinceOptions,
                  vatStatus: vatStatusOptions,
                  grossReceiptsStatus: grossReceiptsStatusOptions,
                  billingCurrency: billingCurrencyOptions,
                  printerCountry: printerCountryOptions,
                  printerProvince: printerProvinceOptions,
                }}
                onPatch={(campo: string, valor: any) => {
                  switch (campo) {
                    case "personType":
                      onPersonTypeChange?.(valor);
                      break;
                    case "legalName":
                      onLegalNameChange?.(valor);
                      break;
                    case "ownPublisher":
                      onOwnPublisherSelected?.(valor);
                      break;
                    case "businessId":
                      onBusinessIdChange?.(valor);
                      break;
                    case "legalEmail":
                      onLegalEmailChange?.(valor);
                      break;
                    case "streetAndNumber":
                      onStreetAndNumberChange?.(valor);
                      break;
                    case "floor":
                      onFloorChange?.(valor);
                      break;
                    case "suite":
                      onSuiteChange?.(valor);
                      break;
                    case "postalCode":
                      onPostalCodeChange?.(valor);
                      break;
                    case "country":
                      onCountrySelected?.(valor);
                      break;
                    case "province":
                      onProvinceSelected?.(valor);
                      break;
                    case "city":
                      onCityChange?.(valor);
                      break;
                    case "vatStatus":
                      onVatStatusSelected?.(valor);
                      break;
                    case "taxId":
                      onTaxIdChange?.(valor);
                      break;
                    case "grossReceiptsStatus":
                      onGrossReceiptsStatusSelected?.(valor);
                      break;
                    case "grossReceiptsId":
                      onGrossReceiptsIdChange?.(valor);
                      break;
                    case "billingEmail":
                      onBillingEmailChange?.(valor);
                      break;
                    case "billingCurrency":
                      onBillingCurrencySelected?.(valor);
                      break;
                    case "printerName":
                      onPrinterNameChange?.(valor);
                      break;
                    case "printerStreetAndNumber":
                      onPrinterStreetAndNumberChange?.(valor);
                      break;
                    case "printerFloor":
                      onPrinterFloorChange?.(valor);
                      break;
                    case "printerSuite":
                      onPrinterSuiteChange?.(valor);
                      break;
                    case "printerPostalCode":
                      onPrinterPostalCodeChange?.(valor);
                      break;
                    case "printerCountry":
                      onPrinterCountrySelected?.(valor);
                      break;
                    case "printerProvince":
                      onPrinterProvinceSelected?.(valor);
                      break;
                    case "printerCity":
                      onPrinterCityChange?.(valor);
                      break;
                    case "sadaicFtpUrl":
                      onSadaicFtpUrlChange?.(valor);
                      break;
                    case "sadaicFtpUser":
                      onSadaicFtpUserChange?.(valor);
                      break;
                    case "sadaicFtpPassword":
                      onSadaicFtpPasswordChange?.(valor);
                      break;
                    case "sadaicEmail":
                      onSadaicEmailChange?.(valor);
                      break;
                  }
                }}
              />

              <GroupContainer
                id="business"
                errorText={tenantProfileCreateStore.getCardErrors("business")}
                title={langStore.groupTenantProfileBusinessLabel}
                collapsible={true}
                expanded={tenantProfileCreateStore.getCardState("business")}
                toggleExpand={() =>
                  tenantProfileCreateStore.toggleCardState("business")
                }
              >
                <FormControl fullWidth>
                  <InputReference
                    id="personType"
                    autoFocus
                    error={Boolean(personTypeError)}
                    helperText={
                      personTypeError ||
                      tenantProfileCreateStore.warnings["personType"]?.message
                    }
                    label={langStore.TenantProfilePersonTypeLabel}
                    options={personTypeOptions}
                    nullText={langStore.optionalFieldHint}
                    selectedValue={
                      personType
                        ? {
                            id: personType,
                            displayId:
                              personTypeOptions.find(
                                (o: { id: string }) => o.id === personType
                              )?.displayId || personType,
                          }
                        : null
                    }
                    onChange={() => {}}
                    onSelect={(
                      value: { id: string; displayId: string } | null
                    ) =>
                      onPersonTypeChange(
                        (value?.id as PersonType | "") ||
                          ("" as PersonType | "")
                      )
                    }
                  />
                  <TenantProfileLegalNameCustomField
                    value={legalName}
                    error={Boolean(legalNameError)}
                    helperText={
                      legalNameError ||
                      tenantProfileCreateStore.warnings["legalName"]?.message
                    }
                    {...{ optional: true }}
                    label={langStore.TenantProfileLegalNameLabel}
                    description={langStore.TenantProfileLegalNameDescription}
                    onChange={(value: string) => onLegalNameChange(value)}
                  />
                  <InputReference
                    id="ownPublisher"
                    label={langStore.TenantProfileOwnPublisherLabel}
                    error={Boolean(ownPublisherError)}
                    helperText={
                      ownPublisherError ||
                      tenantProfileCreateStore.warnings["ownPublisher"]?.message
                    }
                    selectedValue={ownPublisherSelectedValue}
                    onChange={(value: string) => onOwnPublisherChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onOwnPublisherSelected(value)}
                    options={ownPublisherOptions}
                    description={langStore.TenantProfileOwnPublisherDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "publisher_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new PublisherCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <PublisherCreate
                            publisherCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewPublisher"
                        cancelMethod="cancelNewPublisher"
                      />
                    )}
                  />

                  <InputStringMask
                    value={businessId}
                    error={Boolean(businessIdError)}
                    warning={
                      !businessIdError &&
                      Boolean(tenantProfileCreateStore.warnings["businessId"])
                    }
                    helperText={
                      businessIdError ||
                      tenantProfileCreateStore.warnings["businessId"]?.message
                    }
                    label={langStore.TenantProfileBusinessIdLabel}
                    onChange={(value: string) => onBusinessIdChange(value)}
                    maskConfig={{
                      mask: "99-9999999-9",
                      masks: ["99-9999999-9", "99-99999999-9"],
                    }}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileBusinessIdDescription}
                  />

                  <InputString
                    value={legalEmail}
                    error={Boolean(legalEmailError)}
                    warning={
                      !legalEmailError &&
                      Boolean(tenantProfileCreateStore.warnings["legalEmail"])
                    }
                    helperText={
                      legalEmailError ||
                      tenantProfileCreateStore.warnings["legalEmail"]?.message
                    }
                    label={langStore.TenantProfileLegalEmailLabel}
                    onChange={(value: string) => onLegalEmailChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileLegalEmailDescription}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="address"
                errorText={tenantProfileCreateStore.getCardErrors("address")}
                title={langStore.groupTenantProfileAddressLabel}
                collapsible={true}
                expanded={tenantProfileCreateStore.getCardState("address")}
                toggleExpand={() =>
                  tenantProfileCreateStore.toggleCardState("address")
                }
              >
                <FormControl fullWidth>
                  <InputString
                    value={streetAndNumber}
                    error={Boolean(streetAndNumberError)}
                    warning={
                      !streetAndNumberError &&
                      Boolean(
                        tenantProfileCreateStore.warnings["streetAndNumber"]
                      )
                    }
                    helperText={
                      streetAndNumberError ||
                      tenantProfileCreateStore.warnings["streetAndNumber"]
                        ?.message
                    }
                    label={langStore.TenantProfileStreetAndNumberLabel}
                    onChange={(value: string) => onStreetAndNumberChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfileStreetAndNumberDescription
                    }
                  />

                  <InputString
                    value={floor}
                    error={Boolean(floorError)}
                    warning={
                      !floorError &&
                      Boolean(tenantProfileCreateStore.warnings["floor"])
                    }
                    helperText={
                      floorError ||
                      tenantProfileCreateStore.warnings["floor"]?.message
                    }
                    label={langStore.TenantProfileFloorLabel}
                    onChange={(value: string) => onFloorChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileFloorDescription}
                  />

                  <InputString
                    value={suite}
                    error={Boolean(suiteError)}
                    warning={
                      !suiteError &&
                      Boolean(tenantProfileCreateStore.warnings["suite"])
                    }
                    helperText={
                      suiteError ||
                      tenantProfileCreateStore.warnings["suite"]?.message
                    }
                    label={langStore.TenantProfileSuiteLabel}
                    onChange={(value: string) => onSuiteChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileSuiteDescription}
                  />

                  <InputString
                    value={postalCode}
                    error={Boolean(postalCodeError)}
                    warning={
                      !postalCodeError &&
                      Boolean(tenantProfileCreateStore.warnings["postalCode"])
                    }
                    helperText={
                      postalCodeError ||
                      tenantProfileCreateStore.warnings["postalCode"]?.message
                    }
                    label={langStore.TenantProfilePostalCodeLabel}
                    onChange={(value: string) => onPostalCodeChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePostalCodeDescription}
                  />
                  <InputReference
                    id="country"
                    label={langStore.TenantProfileCountryLabel}
                    error={Boolean(countryError)}
                    helperText={
                      countryError ||
                      tenantProfileCreateStore.warnings["country"]?.message
                    }
                    selectedValue={countrySelectedValue}
                    onChange={(value: string) => onCountryChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onCountrySelected(value)}
                    options={countryOptions}
                    description={langStore.TenantProfileCountryDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "country_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new CountryCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <CountryCreate
                            countryCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewCountry"
                        cancelMethod="cancelNewCountry"
                      />
                    )}
                  />
                  <InputReference
                    id="province"
                    label={langStore.TenantProfileProvinceLabel}
                    error={Boolean(provinceError)}
                    helperText={
                      provinceError ||
                      tenantProfileCreateStore.warnings["province"]?.message
                    }
                    selectedValue={provinceSelectedValue}
                    onChange={(value: string) => onProvinceChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onProvinceSelected(value)}
                    options={provinceOptions}
                    description={langStore.TenantProfileProvinceDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "provinceState_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new ProvinceStateCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <ProvinceStateCreate
                            provinceStateCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewProvinceState"
                        cancelMethod="cancelNewProvinceState"
                      />
                    )}
                  />

                  <InputString
                    value={city}
                    error={Boolean(cityError)}
                    warning={
                      !cityError &&
                      Boolean(tenantProfileCreateStore.warnings["city"])
                    }
                    helperText={
                      cityError ||
                      tenantProfileCreateStore.warnings["city"]?.message
                    }
                    label={langStore.TenantProfileCityLabel}
                    onChange={(value: string) => onCityChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileCityDescription}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="billing"
                errorText={tenantProfileCreateStore.getCardErrors("billing")}
                title={langStore.groupTenantProfileBillingLabel}
                collapsible={true}
                expanded={tenantProfileCreateStore.getCardState("billing")}
                toggleExpand={() =>
                  tenantProfileCreateStore.toggleCardState("billing")
                }
              >
                <FormControl fullWidth>
                  <InputReference
                    id="vatStatus"
                    label={langStore.TenantProfileVatStatusLabel}
                    error={Boolean(vatStatusError)}
                    helperText={
                      vatStatusError ||
                      tenantProfileCreateStore.warnings["vatStatus"]?.message
                    }
                    selectedValue={vatStatusSelectedValue}
                    onChange={(value: string) => onVatStatusChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onVatStatusSelected(value)}
                    options={vatStatusOptions}
                    description={langStore.TenantProfileVatStatusDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "vatStatus_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new VatStatusCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <VatStatusCreate
                            vatStatusCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewVatStatus"
                        cancelMethod="cancelNewVatStatus"
                      />
                    )}
                  />

                  <InputString
                    value={taxId}
                    error={Boolean(taxIdError)}
                    warning={
                      !taxIdError &&
                      Boolean(tenantProfileCreateStore.warnings["taxId"])
                    }
                    helperText={
                      taxIdError ||
                      tenantProfileCreateStore.warnings["taxId"]?.message
                    }
                    label={langStore.TenantProfileTaxIdLabel}
                    onChange={(value: string) => onTaxIdChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileTaxIdDescription}
                  />
                  <InputReference
                    id="grossReceiptsStatus"
                    label={langStore.TenantProfileGrossReceiptsStatusLabel}
                    error={Boolean(grossReceiptsStatusError)}
                    helperText={
                      grossReceiptsStatusError ||
                      tenantProfileCreateStore.warnings["grossReceiptsStatus"]
                        ?.message
                    }
                    selectedValue={grossReceiptsStatusSelectedValue}
                    onChange={(value: string) =>
                      onGrossReceiptsStatusChange(value)
                    }
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onGrossReceiptsStatusSelected(value)}
                    options={grossReceiptsStatusOptions}
                    description={
                      langStore.TenantProfileGrossReceiptsStatusDescription
                    }
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "grossReceiptsStatus_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new GrossReceiptsStatusCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <GrossReceiptsStatusCreate
                            grossReceiptsStatusCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewGrossReceiptsStatus"
                        cancelMethod="cancelNewGrossReceiptsStatus"
                      />
                    )}
                  />

                  <InputString
                    value={grossReceiptsId}
                    error={Boolean(grossReceiptsIdError)}
                    warning={
                      !grossReceiptsIdError &&
                      Boolean(
                        tenantProfileCreateStore.warnings["grossReceiptsId"]
                      )
                    }
                    helperText={
                      grossReceiptsIdError ||
                      tenantProfileCreateStore.warnings["grossReceiptsId"]
                        ?.message
                    }
                    label={langStore.TenantProfileGrossReceiptsIdLabel}
                    onChange={(value: string) => onGrossReceiptsIdChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfileGrossReceiptsIdDescription
                    }
                  />

                  <InputString
                    value={billingEmail}
                    error={Boolean(billingEmailError)}
                    warning={
                      !billingEmailError &&
                      Boolean(tenantProfileCreateStore.warnings["billingEmail"])
                    }
                    helperText={
                      billingEmailError ||
                      tenantProfileCreateStore.warnings["billingEmail"]?.message
                    }
                    label={langStore.TenantProfileBillingEmailLabel}
                    onChange={(value: string) => onBillingEmailChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileBillingEmailDescription}
                  />
                  <InputReference
                    id="billingCurrency"
                    label={langStore.TenantProfileBillingCurrencyLabel}
                    error={Boolean(billingCurrencyError)}
                    helperText={
                      billingCurrencyError ||
                      tenantProfileCreateStore.warnings["billingCurrency"]
                        ?.message
                    }
                    selectedValue={billingCurrencySelectedValue}
                    onChange={(value: string) => onBillingCurrencyChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onBillingCurrencySelected(value)}
                    options={billingCurrencyOptions}
                    description={
                      langStore.TenantProfileBillingCurrencyDescription
                    }
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "currency_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new CurrencyCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <CurrencyCreate
                            currencyCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewCurrency"
                        cancelMethod="cancelNewCurrency"
                      />
                    )}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="printer"
                errorText={tenantProfileCreateStore.getCardErrors("printer")}
                title={langStore.groupTenantProfilePrinterLabel}
                collapsible={true}
                expanded={tenantProfileCreateStore.getCardState("printer")}
                toggleExpand={() =>
                  tenantProfileCreateStore.toggleCardState("printer")
                }
              >
                <FormControl fullWidth>
                  <InputString
                    value={printerName}
                    error={Boolean(printerNameError)}
                    warning={
                      !printerNameError &&
                      Boolean(tenantProfileCreateStore.warnings["printerName"])
                    }
                    helperText={
                      printerNameError ||
                      tenantProfileCreateStore.warnings["printerName"]?.message
                    }
                    label={langStore.TenantProfilePrinterNameLabel}
                    onChange={(value: string) => onPrinterNameChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePrinterNameDescription}
                  />

                  <InputString
                    value={printerStreetAndNumber}
                    error={Boolean(printerStreetAndNumberError)}
                    warning={
                      !printerStreetAndNumberError &&
                      Boolean(
                        tenantProfileCreateStore.warnings[
                          "printerStreetAndNumber"
                        ]
                      )
                    }
                    helperText={
                      printerStreetAndNumberError ||
                      tenantProfileCreateStore.warnings[
                        "printerStreetAndNumber"
                      ]?.message
                    }
                    label={langStore.TenantProfilePrinterStreetAndNumberLabel}
                    onChange={(value: string) =>
                      onPrinterStreetAndNumberChange(value)
                    }
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfilePrinterStreetAndNumberDescription
                    }
                  />

                  <InputString
                    value={printerFloor}
                    error={Boolean(printerFloorError)}
                    warning={
                      !printerFloorError &&
                      Boolean(tenantProfileCreateStore.warnings["printerFloor"])
                    }
                    helperText={
                      printerFloorError ||
                      tenantProfileCreateStore.warnings["printerFloor"]?.message
                    }
                    label={langStore.TenantProfilePrinterFloorLabel}
                    onChange={(value: string) => onPrinterFloorChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePrinterFloorDescription}
                  />

                  <InputString
                    value={printerSuite}
                    error={Boolean(printerSuiteError)}
                    warning={
                      !printerSuiteError &&
                      Boolean(tenantProfileCreateStore.warnings["printerSuite"])
                    }
                    helperText={
                      printerSuiteError ||
                      tenantProfileCreateStore.warnings["printerSuite"]?.message
                    }
                    label={langStore.TenantProfilePrinterSuiteLabel}
                    onChange={(value: string) => onPrinterSuiteChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePrinterSuiteDescription}
                  />

                  <InputString
                    value={printerPostalCode}
                    error={Boolean(printerPostalCodeError)}
                    warning={
                      !printerPostalCodeError &&
                      Boolean(
                        tenantProfileCreateStore.warnings["printerPostalCode"]
                      )
                    }
                    helperText={
                      printerPostalCodeError ||
                      tenantProfileCreateStore.warnings["printerPostalCode"]
                        ?.message
                    }
                    label={langStore.TenantProfilePrinterPostalCodeLabel}
                    onChange={(value: string) =>
                      onPrinterPostalCodeChange(value)
                    }
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfilePrinterPostalCodeDescription
                    }
                  />
                  <InputReference
                    id="printerCountry"
                    label={langStore.TenantProfilePrinterCountryLabel}
                    error={Boolean(printerCountryError)}
                    helperText={
                      printerCountryError ||
                      tenantProfileCreateStore.warnings["printerCountry"]
                        ?.message
                    }
                    selectedValue={printerCountrySelectedValue}
                    onChange={(value: string) => onPrinterCountryChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onPrinterCountrySelected(value)}
                    options={printerCountryOptions}
                    description={
                      langStore.TenantProfilePrinterCountryDescription
                    }
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "country_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new CountryCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <CountryCreate
                            countryCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewCountry"
                        cancelMethod="cancelNewCountry"
                      />
                    )}
                  />
                  <InputReference
                    id="printerProvince"
                    label={langStore.TenantProfilePrinterProvinceLabel}
                    error={Boolean(printerProvinceError)}
                    helperText={
                      printerProvinceError ||
                      tenantProfileCreateStore.warnings["printerProvince"]
                        ?.message
                    }
                    selectedValue={printerProvinceSelectedValue}
                    onChange={(value: string) => onPrinterProvinceChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onPrinterProvinceSelected(value)}
                    options={printerProvinceOptions}
                    description={
                      langStore.TenantProfilePrinterProvinceDescription
                    }
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "provinceState_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new ProvinceStateCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <ProvinceStateCreate
                            provinceStateCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewProvinceState"
                        cancelMethod="cancelNewProvinceState"
                      />
                    )}
                  />

                  <InputString
                    value={printerCity}
                    error={Boolean(printerCityError)}
                    warning={
                      !printerCityError &&
                      Boolean(tenantProfileCreateStore.warnings["printerCity"])
                    }
                    helperText={
                      printerCityError ||
                      tenantProfileCreateStore.warnings["printerCity"]?.message
                    }
                    label={langStore.TenantProfilePrinterCityLabel}
                    onChange={(value: string) => onPrinterCityChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfilePrinterCityDescription}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="integrationsSadaic"
                errorText={tenantProfileCreateStore.getCardErrors(
                  "integrationsSadaic"
                )}
                title={langStore.groupTenantProfileIntegrationsSadaicLabel}
                description={
                  langStore.groupTenantProfileIntegrationsSadaicDescription
                }
                collapsible={true}
                expanded={tenantProfileCreateStore.getCardState(
                  "integrationsSadaic"
                )}
                toggleExpand={() =>
                  tenantProfileCreateStore.toggleCardState("integrationsSadaic")
                }
              >
                <FormControl fullWidth>
                  <InputString
                    value={sadaicFtpUrl}
                    error={Boolean(sadaicFtpUrlError)}
                    warning={
                      !sadaicFtpUrlError &&
                      Boolean(tenantProfileCreateStore.warnings["sadaicFtpUrl"])
                    }
                    helperText={
                      sadaicFtpUrlError ||
                      tenantProfileCreateStore.warnings["sadaicFtpUrl"]?.message
                    }
                    label={langStore.TenantProfileSadaicFtpUrlLabel}
                    onChange={(value: string) => onSadaicFtpUrlChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileSadaicFtpUrlDescription}
                  />

                  <InputString
                    value={sadaicFtpUser}
                    error={Boolean(sadaicFtpUserError)}
                    warning={
                      !sadaicFtpUserError &&
                      Boolean(
                        tenantProfileCreateStore.warnings["sadaicFtpUser"]
                      )
                    }
                    helperText={
                      sadaicFtpUserError ||
                      tenantProfileCreateStore.warnings["sadaicFtpUser"]
                        ?.message
                    }
                    label={langStore.TenantProfileSadaicFtpUserLabel}
                    onChange={(value: string) => onSadaicFtpUserChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.TenantProfileSadaicFtpUserDescription
                    }
                  />

                  <InputSecret
                    value={sadaicFtpPassword}
                    error={Boolean(sadaicFtpPasswordError)}
                    helperText={
                      sadaicFtpPasswordError ||
                      tenantProfileCreateStore.warnings["sadaicFtpPassword"]
                        ?.message
                    }
                    label={langStore.TenantProfileSadaicFtpPasswordLabel}
                    onChange={(value: string) =>
                      onSadaicFtpPasswordChange(value)
                    }
                    description={
                      langStore.TenantProfileSadaicFtpPasswordDescription
                    }
                  />

                  <InputString
                    value={sadaicEmail}
                    error={Boolean(sadaicEmailError)}
                    warning={
                      !sadaicEmailError &&
                      Boolean(tenantProfileCreateStore.warnings["sadaicEmail"])
                    }
                    helperText={
                      sadaicEmailError ||
                      tenantProfileCreateStore.warnings["sadaicEmail"]?.message
                    }
                    label={langStore.TenantProfileSadaicEmailLabel}
                    onChange={(value: string) => onSadaicEmailChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.TenantProfileSadaicEmailDescription}
                  />
                </FormControl>
              </GroupContainer>
            </FormRecordProvider>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
